import {
  Anchor,
  Box,
  Group,
  Table,
  Text,
  Title,
  Tooltip,
  useMantineColorScheme,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import classes from "./PerformingAssets.module.css";

import {
  EmptyState,
  ListTable,
  ListTableTh,
  TextSerif,
  useDataFilter,
} from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { Currency } from "@/types/currency";
import { formatCurrency, formatNumber, toPercentFraction } from "@/utils/formatEuro";

import type { MarketCar, MarketCarColumns } from "@/types/market";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type PerformingAssetsProps = {
  marketCars?: MarketCar[];
  columns: MarketCarColumns[];
  getPath?: (carId: string) => string;
  isMobile?: boolean;
  makeOrDecade: string;
  filterFields?: string[];
} & MantineStyleProps;

export const PerformingAssets: FC<PerformingAssetsProps> = ({
  marketCars = [],
  columns,
  getPath,
  isMobile = false,
  makeOrDecade,
  filterFields = [
    "model_name",
    "make_id",
    "produced_cars",
    "production_years",
    "current_valuation",
    "year_over_year_gain_loss",
    "variant",
  ],
}) => {
  const { t } = useTranslation();
  const makeOrDecadeLabel = t(`market.tabs.${makeOrDecade}`, makeOrDecade);

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";
  const textColor = isDark ? "#a7a7a7A" : "secondary.4";

  const searchFilter = (car: any, query: string) => {
    const q = query.toLowerCase();
    return filterFields.some((field) => {
      const value = car[field];
      if (value == null) return false;
      return String(value).toLowerCase().includes(q);
    });
  };

  const { data: filteredData } = useDataFilter(marketCars, {
    search: searchFilter,
  });

  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a["year_over_year_gain_loss"];
    const bValue = b["year_over_year_gain_loss"];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    return String(bValue).localeCompare(String(aValue)); // "desc" order active
  });

  const limitedData = sortedData.slice(0, 5);

  const excludedColumns: MarketCarColumns[] = ["produced_cars", "production_years", "make_id"];

  let enabledColumns = columns.filter((id) => !excludedColumns.includes(id));

  const ytdIndex = enabledColumns.indexOf("year_over_year_gain_loss");
  if (ytdIndex !== -1) enabledColumns.splice(ytdIndex, 1);
  const valuationIndex = enabledColumns.indexOf("current_valuation");
  if (valuationIndex !== -1) enabledColumns.splice(valuationIndex, 1);

  /* Table columns order */
  enabledColumns = [...enabledColumns, "year_over_year_gain_loss", "current_valuation"];

  const tableColumns = enabledColumns.map(
    (id, index) =>
      ({
        id,
        label:
          id === "year_over_year_gain_loss" || id === "current_valuation" ? (
            <TextSerif size="xs" c={textColor}>
              {t(`market.table.cols.${id}`)}
            </TextSerif>
          ) : (
            t(`market.table.cols.${id}`)
          ),
        thProps: index > 0 ? { style: { textAlign: "right" }, w: 1, pl: "lg" } : undefined,
      }) as ListTableTh
  );

  const fontSize = { base: "xs", sm: "sm" };

  return (
    <Box className={classes["css-performing-assets"]} p="md">
      <Title order={4}>{t("market.top_performers", { makeOrDecade: makeOrDecadeLabel })}</Title>
      <ListTable.Context columns={tableColumns} namespace="performing-assets-table">
        {({ headingColumns }) => {
          const columnsId = headingColumns.map(({ id }) => id);

          return limitedData.length > 0 ? (
            <ListTable
              minWidth={0}
              tableProps={{
                highlightOnHover: false,
                striped: false,
                horizontalSpacing: isMobile ? 1 : 0,
              }}
              data={{
                head: isMobile ? undefined : headingColumns,
                body: limitedData.map((car: MarketCar, index: number) => (
                  <Table.Tr key={`Row_${car.model_name}_${index}`}>
                    {columnsId.map((column) => (
                      <Table.Td key={`Cell_${column}`} ta="right">
                        {(() => {
                          try {
                            switch (column as MarketCarColumns) {
                              case "model_name":
                                return (
                                  <Group align="center" gap="sm">
                                    <Text size="xs" fw="lighter" c="secondary">
                                      {index + 1}.
                                    </Text>

                                    {getPath ? (
                                      isMobile ? (
                                        <Tooltip
                                          events={{ hover: true, focus: true, touch: true }}
                                          label={`${car.make_name} ${car.model_name}${
                                            car.variant ? ` (${car.variant})` : ""
                                          }`}
                                          position="top-start"
                                          offset={4}
                                          maw={300}
                                          openDelay={100}
                                          withArrow
                                          multiline
                                        >
                                          <Anchor
                                            component={Link}
                                            fz={fontSize}
                                            to={getPath(car.id)}
                                            lineClamp={1}
                                            ta="left"
                                            style={{ flex: 1, minWidth: 0 }}
                                          >
                                            {car.make_name} {car.model_name}
                                            {car.variant ? ` (${car.variant})` : ""}
                                          </Anchor>
                                        </Tooltip>
                                      ) : (
                                        <Anchor
                                          component={Link}
                                          fz={fontSize}
                                          to={getPath(car.id)}
                                          lineClamp={1}
                                          ta="left"
                                          style={{ flex: 1, minWidth: 0 }}
                                        >
                                          {car.make_name} {car.model_name}
                                          {car.variant ? ` (${car.variant})` : ""}
                                        </Anchor>
                                      )
                                    ) : isMobile ? (
                                      <Tooltip
                                        events={{ hover: true, focus: true, touch: true }}
                                        label={`${car.make_name} ${car.model_name}${
                                          car.variant ? ` (${car.variant})` : ""
                                        }`}
                                        position="top-start"
                                        offset={4}
                                        maw={300}
                                        openDelay={100}
                                        withArrow
                                        multiline
                                      >
                                        <Text
                                          size="sm"
                                          lineClamp={1}
                                          style={{ flex: 1, minWidth: 0 }}
                                        >
                                          {car.model_name}
                                          {car.variant ? ` (${car.variant})` : ""}
                                        </Text>
                                      </Tooltip>
                                    ) : (
                                      <Text
                                        size="sm"
                                        lineClamp={1}
                                        style={{ flex: 1, minWidth: 0 }}
                                      >
                                        {car.model_name}
                                        {car.variant ? ` (${car.variant})` : ""}
                                      </Text>
                                    )}
                                  </Group>
                                );

                              case "year_over_year_gain_loss":
                                return (
                                  <Text
                                    fz={fontSize}
                                    c={
                                      car["year_over_year_gain_loss"] > 0
                                        ? "success"
                                        : car["year_over_year_gain_loss"] < 0
                                          ? "danger"
                                          : undefined
                                    }
                                  >
                                    {car["year_over_year_gain_loss"] > 0 ? "+" : ""}
                                    {formatNumber(
                                      toPercentFraction(car["year_over_year_gain_loss"] ?? 0),
                                      {
                                        style: "percent",
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </Text>
                                );

                              case "current_valuation":
                                return (
                                  <Text component="span" fz={fontSize} pl={{ base: "sm", sm: 0 }}>
                                    {formatCurrency(car["current_valuation"], currency.code)}
                                  </Text>
                                );

                              default:
                                const value = car[column as MarketCarColumns];
                                if (Array.isArray(value) || typeof value === "object") {
                                  return null; // Skip non-renderable types
                                }
                                return value;
                            }
                          } catch {
                            return null;
                          }
                        })()}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                )),
              }}
            />
          ) : (
            <EmptyState type="noResults" text={t("components.WatchListTable.NoResults")} />
          );
        }}
      </ListTable.Context>
    </Box>
  );
};
